import axios from 'axios';
import { PUBLIC_INTEREST_URL, PUBLIC_ORDER_URL } from '@/services/urls';

// Everything the public website says that may need changing before it goes
// live — numbers, cities, phones, screenshots, integrations — lives here, so
// it can be edited without touching the page components.

// Main contact number: the one the previous landing page used for every
// «Καλέστε μας» button.
export const CONTACT = {
	phone: '+306987951369',
	phoneDisplay: '698 795 1369',
};

// The business behind DeliveryMan, as registered in ΓΕΜΗ (for the privacy
// policy). The Greek legal name is the registry's Latin one written in Greek.
export const COMPANY = {
	legalName: 'ΚΩΤΟΥΛΑΣ ΛΑΖΑΡΟΣ ΤΟΥ ΖΗΣΗ',
	tradeName: 'Delivery Man',
	afm: '113278106',
	gemi: '139921536000',
	address: 'Κορυτσάς 22, Καστοριά',
};

export const PRIVACY_META = {
	title: 'Πολιτική απορρήτου — DeliveryMan',
	description:
		'Ποια προσωπικά δεδομένα συλλέγει το DeliveryMan, γιατί, για πόσο τα κρατά και ποια δικαιώματα έχετε.',
};

// Cities where Deliveryman runs today, with the number shops call there.
// Copied from the previous landing page. Add a city only once it's confirmed.
// Every city shows whose service it is: Deliveryman's own logo, or — for a
// city run by a partner under its own brand on Deliveryman's platform — the
// partner's logo with «(powered by DeliveryMan)» under it (`brand` below).
export const OWN_BRAND = {
	name: 'DeliveryMan',
	logo: '/images/logo_rect.png',
	width: 1419,
	height: 319,
};

export const BRANDS = {
	solvd: { name: 'Solvd', logo: '/images/site/solvd-logo.webp', width: 360, height: 150 },
	af: {
		name: 'AF Delivery Services',
		logo: '/images/site/af-delivery-logo.webp',
		width: 360,
		height: 183,
	},
};

export const CITIES = [
	{ name: 'Τρίκαλα', phone: '+306981984310', display: '698 198 4310' },
	{ name: 'Βόλος', phone: '+306988939498', display: '698 893 9498' },
	{ name: 'Καστοριά', phone: '+306987951369', display: '698 795 1369' },
	{ name: 'Καρδίτσα', phone: '+306939671392', display: '693 967 1392' },
	{ name: 'Καλαμπάκα', phone: '+306956020510', display: '695 602 0510' },
	{ name: 'Φλώρινα', phone: '+306987686857', display: '698 768 6857', brand: 'af' },
	{ name: 'Θεσσαλονίκη', phone: '+302312318352', display: '2312 318352', brand: 'solvd' },
];

// Social-proof numbers. `value: null` hides a figure until a real number is
// filled in. Orders and shops are the figures the previous landing page
// showed; confirm them before production.
export const STATS = [
	{ key: 'orders', value: 2900000, label: 'Παραγγελίες' },
	{ key: 'shops', value: 550, label: 'Καταστήματα' },
	{ key: 'riders', value: null, label: 'Διανομείς' },
	{ key: 'cities', value: CITIES.length, label: 'Πόλεις', exact: true },
];

// Real product screenshots in public/images/site/ (webp). The Live ones were
// taken on DEV with demo shops, riders and customers (no real data); they are
// 800px wide, so a sharper capture can replace them later. The phone shows
// the rider app's own launch screen (its splash: logo on white, from
// app-seler/assets) until a screenshot from a phone replaces it. A frame with
// no path shows a placeholder.
export const SCREENSHOTS = {
	dashboard: '/images/site/dashboard-live.webp',
	riderApp: '/images/site/rider-app-splash.webp',
	live: '/images/site/live-detail.webp',
	statistics: null,
};

// Logos of systems Deliveryman is actually connected to. Leave empty until
// each one is confirmed; the section then shows only the generic categories.
// Shape: { name: 'X', logo: '/images/...', href?: 'https://...' }
export const INTEGRATION_LOGOS = [];

export const INTEGRATION_KINDS = [
	{
		title: 'Πλατφόρμες παραγγελιών',
		text: 'Οι παραγγελίες έρχονται κατευθείαν στο DeliveryMan, χωρίς να ξαναγράφονται.',
	},
	{
		title: 'Συστήματα παραγγελιοληψίας & POS',
		text: 'Σύνδεση με το πρόγραμμα που ήδη χρησιμοποιεί το κατάστημα.',
	},
	{
		title: 'Site & εφαρμογή καταστήματος',
		text: 'Οι online παραγγελίες του καταστήματος περνούν αυτόματα στη διανομή.',
	},
	{
		title: 'Ανοιχτό API',
		text: 'Τεκμηριωμένο API για κάθε σύστημα που θέλει να στέλνει παραγγελίες.',
	},
];

// Interest form: who is getting in touch.
export const ROLES = {
	shop: { label: 'Κατάστημα', subject: 'Εξυπηρέτηση καταστήματος' },
	rider: { label: 'Διανομέας', subject: 'Αίτηση διανομέα' },
	fleet: { label: 'Στόλος / συνεργάτης', subject: 'Παρουσίαση για στόλο' },
};

// Sends the interest form to the server, where masters read it in
// Επισκόπηση → «Αιτήματα από το site». Resolves { ok: true } or { error } (a
// message from the server, or null) — never throws.
export const sendInterest = async fields => {
	try {
		const res = await axios.post(PUBLIC_INTEREST_URL, fields);
		if (res?.data?.ok) return { ok: true };
		return { error: typeof res?.data?.error === 'string' ? res.data.error : null };
	} catch {
		return { error: null };
	}
};

export const PAGE_META = {
	title: 'DeliveryMan — Πλατφόρμα διαχείρισης delivery',
	description:
		'Ολοκληρωμένη πλατφόρμα διαχείρισης delivery για καταστήματα, διανομείς και στόλους: παραγγελίες, διανομείς, έξυπνες αναθέσεις, live χάρτης και στατιστικά.',
};

// The order page for private customers (/paraggelia). The server finds the
// city from the location and puts the order on that city's Live; it never
// says more than the city's name.
const postOrder = async (path, fields) => {
	try {
		const res = await axios.post(PUBLIC_ORDER_URL + path, fields);
		if (res?.data?.ok) return { ok: true, ...res.data };
		return {
			error: typeof res?.data?.error === 'string' ? res.data.error : null,
			code: res?.data?.code,
			// A paused city: when it opens by itself, if it does.
			opening: res?.data?.opening,
		};
	} catch {
		return { error: null };
	}
};

export const findOrderCity = location => postOrder('city', location);
export const sendPublicOrder = fields => postOrder('', fields);

export const ORDER_META = {
	title: 'Στείλτε παραγγελία — DeliveryMan',
	description:
		'Γράψτε τι θέλετε και πού, και ένας διανομέας του DeliveryMan το φέρνει. Πληρωμή με μετρητά στον διανομέα.',
};

export const AREAS_META = {
	title: 'Περιοχές — DeliveryMan',
	description:
		'Οι πόλεις όπου λειτουργεί το DeliveryMan και πώς να εκδηλώσεις ενδιαφέρον για τη δική σου περιοχή.',
};
